import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Inject,
  inject,
  input,
  Input,
  Optional,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { BehaviorSubject, combineLatest, firstValueFrom, map, of, switchMap } from 'rxjs';
import { ConversationService } from '../../../../../../core/src/lib/state/conversation.service';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import {
  ConversationUnseen,
  KabobDynamicButton,
} from '../../../../../../core/src/lib/interface/conversation.interface';
import { HostAppInterface } from '../../../../../../core/src/lib/interface/host-app.interface';
import {
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
} from '../../../../../../core/src/lib/tokens';
import { ViewModeService } from '../../../../../../core/src/lib/view-mode.service';
import { InboxConversationAssigneeComponent } from '../../../../components/inbox-conversation-assignee/inbox-conversation-assignee.component';
import { InboxConversationSubtitleAccountComponent } from '../../../../components/inbox-conversation-subtitle/inbox-conversation-subtitle.component';
import { InboxConversationTitleComponent } from '../../../../components/inbox-conversation-title/inbox-conversation-title.component';
import { INBOX_NAVIGATION_LINK, INBOX_NAVIGATION_LINK_FULLSCREEN } from '../../../../constants';
import { InboxInfoDrawerService } from '../inbox-info-pane/inbox-info-drawer.service';
import { MatDialog } from '@angular/material/dialog';
import { ConversationApiService, ParticipantType } from '@vendasta/conversation';
import { AIResponseMode } from '@vendasta/protos/conversation/v1';
import { ConversationChannelValue } from '@vendasta/protos/conversation/v1/enums';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  aiModeLabelKey,
  getAvailableAiModes,
  resolveCurrentAiMode,
  toAiResponseMode,
} from '../../../../../../core/src/lib/ai-mode.util';
import { isMobile } from '../../../../../../core/src/lib/inbox-utils';

@Component({
  selector: 'inbox-messages-top-bar',
  templateUrl: './inbox-messages-top-bar.component.html',
  styleUrls: ['./inbox-messages-top-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    GalaxyBadgeModule,
    InboxConversationAssigneeComponent,
    InboxConversationTitleComponent,
    TranslateModule,
    InboxConversationSubtitleAccountComponent,
  ],
})
export class InboxMessagesTopBarComponent {
  conversationDetail$ = this.conversationService.currentConversationDetail$;
  kabobAvailableActions$ = this.conversationDetail$.pipe(
    switchMap((conversationDetail) => {
      if (!conversationDetail) return of(new Array<KabobDynamicButton>());
      return this.hostAppInterface.getKabobAvailableActions(conversationDetail);
    }),
  );

  isInternalInfoDeleted = false;
  unseen$$ = new BehaviorSubject<ConversationUnseen>({ read: true, conversationId: '' });
  unseen$ = this.unseen$$.asObservable();

  readonly showInboxViews$ = this.inboxService.showInboxViews$;

  readonly showInboxBCSideBar = toSignal(this.inboxService.showInboxBCSideBar$);
  readonly canAccessInboxSidebar = computed(() => this.showInboxBCSideBar() || this.inboxService.isPartnerCenter);

  readonly isBusinessApp = this.inboxService.isBusinessApp;

  readonly displayAssignee$ = this.inboxService.useFilteredInbox$.pipe(
    map((flagOn) => !flagOn && (this.inboxService.isSalesCenter || this.inboxService.isPartnerCenter)),
  );

  // Legacy boolean AI-replies control, still rendered whenever the mode pill is hidden so that
  // ai_drafter_enabled=off is exactly today's behaviour rather than a missing control.
  readonly showAIResponseToggle$ = this.conversationService.aiResponderForConversation$.pipe(
    map((aiResponder) => !!aiResponder?.isEnabled),
  );

  private readonly $hasAiResponder = toSignal(this.showAIResponseToggle$, { initialValue: false });

  readonly aiResponseStatus = toSignal(
    this.conversationDetail$.pipe(map((detail) => detail?.conversation.aiConfiguration?.aiResponseEnabled ?? false)),
    { requireSync: true },
  );

  // The class SDK's AIConfiguration doesn't declare aiResponseMode, but its fromProto does
  // Object.assign(m, proto), so the field the backend sends survives untyped — hence the cast.
  private readonly $aiResponseMode = toSignal(
    this.conversationDetail$.pipe(
      map((detail) =>
        toAiResponseMode(
          (detail?.conversation.aiConfiguration as { aiResponseMode?: unknown } | undefined)?.aiResponseMode,
        ),
      ),
    ),
    { requireSync: true },
  );

  private readonly $channel = toSignal(this.conversationDetail$.pipe(map((detail) => detail?.conversation.channel)), {
    requireSync: true,
  });

  private readonly $aiDrafterEnabled = toSignal(this.inboxService.featureFlagAiDrafter$, { initialValue: false });

  readonly isPersonalEmailConversation = computed(
    // $channel is the numeric class-SDK enum; the protos SDK's ConversationChannel is a string enum,
    // so compare against its numeric companion map (same proto, same values).
    () => (this.$channel() as number | undefined) === ConversationChannelValue.CONVERSATION_CHANNEL_PERSONAL_EMAIL,
  );

  // Every channel, so AutoDraft is selectable on the non-email channels the backend now supports.
  // Requires a responder like the legacy toggle does — without one nothing would act on the mode.
  readonly showAiModePill = computed(() => this.$aiDrafterEnabled() && this.$hasAiResponder());

  // Derived only from the conversation, which is also what the composer's AI status strip reads, so
  // the two can't disagree — including when the strip's own Turn on writes the legacy bool.
  readonly currentAiMode = computed(() => resolveCurrentAiMode(this.$aiResponseMode(), this.aiResponseStatus()));
  readonly currentAiModeLabelKey = computed(() => aiModeLabelKey(this.currentAiMode()));
  readonly availableAiModes = computed(() => getAvailableAiModes(this.isPersonalEmailConversation()));

  readonly $showCloseConversationButton = toSignal(
    combineLatest([this.conversationDetail$, this.inboxService.useFilteredInbox$]).pipe(
      map(([detail, filteredInboxEnabled]) => {
        if (filteredInboxEnabled) return true;
        return !detail?.participants?.some((p) => p.participantType === ParticipantType.PARTICIPANT_TYPE_ANONYMOUS);
      }),
    ),
  );

  readonly $conversationFullScreenFeatureFlag = toSignal(this.inboxService.featureFlagFullscreenInboxPC$);

  readonly $showConversationOverlayButton = computed(
    () => !this.isConversationOverlayMode() && this.$conversationFullScreenFeatureFlag() && !isMobile(),
  );

  @Input() set unseen(value: ConversationUnseen) {
    this.unseen$$.next(value);
  }

  @Input() isFollowing = false;
  @Input() isOpen = false;
  isConversationOverlayMode = input<boolean>(false);

  useSidebarStyles$ = this.viewModeService.viewMode$.pipe(map((viewMode) => viewMode === 'sidebar'));

  readonly $showInboxBack = computed(() => {
    if (this.isConversationOverlayMode()) {
      return false;
    }

    return isMobile() || this.inboxService.isVendorCenter;
  });

  @Input() accountGroupId = '';
  @Input() groupId = '';

  @Output() markedUnread = new EventEmitter();
  @Output() changeFollowStatus = new EventEmitter<boolean>();
  @Output() changeOpenStatus = new EventEmitter<boolean>();
  @Output() titleInBusinessAppClicked = new EventEmitter<boolean>();
  @Output() changeAIResponseEnabled = new EventEmitter<boolean>();
  @Output() openConversationOverlayClicked = new EventEmitter<boolean>();
  @Output() closeConversationOverlayClicked = new EventEmitter<boolean>();

  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  constructor(
    private router: Router,
    private conversationService: ConversationService,
    private inboxService: InboxService,
    @Optional()
    private viewModeService: ViewModeService,
    private inboxInfoDrawerService: InboxInfoDrawerService,
    @Inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN) private readonly hostAppInterface: HostAppInterface,
    private readonly dialog: MatDialog,
    private conversationAPIService: ConversationApiService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
  ) {}

  async back(): Promise<void> {
    if (this.routes()?.useModal) {
      this.conversationService.removeLastConversationId();
      this.router.navigate(
        this.$conversationFullScreenFeatureFlag() ? INBOX_NAVIGATION_LINK_FULLSCREEN : INBOX_NAVIGATION_LINK,
        {
          queryParamsHandling: 'merge',
          queryParams: { inboxPrefilledMessage: null },
        },
      );
      return;
    }

    const path = this.accountGroupId
      ? `/account/location/${this.accountGroupId}/inbox`
      : this.groupId
        ? `/account/brands/${this.groupId}/inbox`
        : '/inbox';
    this.router.navigate([path], {
      queryParamsHandling: 'merge',
      queryParams: { inboxPrefilledMessage: null },
    });
  }

  markUnread(): void {
    this.markedUnread.emit({
      read: false,
    });
  }

  markRead(): void {
    this.markedUnread.emit({
      read: true,
    });
  }

  updateFollowStatus(): void {
    this.changeFollowStatus.emit(!this.isFollowing);
  }

  async toggleDetailsDrawer(): Promise<void> {
    if (this.showInboxBCSideBar()) {
      this.titleInBusinessAppClicked.emit(true);
    }
    this.inboxInfoDrawerService.toggle();
  }

  toggleConversationOpen(): void {
    this.changeOpenStatus.emit(!this.isOpen);
  }

  // Retained for the flag-off path; superseded by setAiMode once ai_drafter_enabled is on everywhere.
  async toggleAIResponseStatus(): Promise<void> {
    const conversation = await firstValueFrom(this.conversationDetail$);
    if (!conversation) return;

    try {
      await this.conversationService.setConversationAIResponderStatus(
        conversation.conversation?.conversationId,
        !this.aiResponseStatus(),
      );
    } catch (e) {
      console.error('error enabling/disabling ai response in the conversation', e);
      this.snackbarService.openErrorSnack(this.translateService.instant('INBOX.SOMETHING_WENT_WRONG'));
    }
  }

  async setAiMode(mode: AIResponseMode): Promise<void> {
    if (mode === this.currentAiMode()) return;

    const conversation = await firstValueFrom(this.conversationDetail$);
    const conversationId = conversation?.conversation?.conversationId;
    if (!conversationId) return;

    try {
      await this.conversationService.setConversationAIResponseMode(conversationId, mode);
    } catch (e) {
      console.error('error setting ai response mode in the conversation', e);
      this.snackbarService.openErrorSnack(this.translateService.instant('INBOX.SOMETHING_WENT_WRONG'));
    }
  }

  closeConversationOverlay(): void {
    this.closeConversationOverlayClicked.emit(true);
  }

  async openConversationOverlay(): Promise<void> {
    this.openConversationOverlayClicked.emit(true);
  }

  get isMobile(): boolean {
    return isMobile();
  }
}
